import { createTheme } from "@mui/material/styles";
import { store } from "../../Redux/store";
import { COLORS, GRADIENTS, SHADOWS, IMPORTANT_VALUES } from "./colors";
// First fix the createStrictColorPalette function to return the palette
function createStrictColorPalette(initialValues = {}) {
  const usedColors = new Set();
  const colorPalette = {};

  const isSpecialValue = (value) => {
    return (
      typeof value !== "string" ||
      value.includes("gradient") ||
      value.includes("!important")
    );
  };

  for (const [key, value] of Object.entries(initialValues)) {
    if (isSpecialValue(value)) {
      colorPalette[key] = value;
      continue;
    }
    const normalizedValue = value.toUpperCase();
    if (usedColors.has(normalizedValue)) {
      const errorMsg = `Duplicate color value "${value}" detected for key "${key}". Color already used.`;
      if (process.env.NODE_ENV === "development") {
        throw new Error(errorMsg);
        // console.warn(errorMsg);
      } else {
        console.error(
          "Color duplication detected (ignored in production):",
          errorMsg
        );
      }
    }
    usedColors.add(normalizedValue);
    colorPalette[key] = value;
  }

  return colorPalette; // Return the palette
}

const VALIDATED_COLORS = createStrictColorPalette(COLORS);

const SHARED_VALUES = {
  SHADOW_LEARNING_TOPIC: SHADOWS.LEARNING_TOPIC_SHADOW,
  ACTIVE_BTN_COLOR: COLORS.ACTIVE_BTN,
  PUBLISHED_GREEN: COLORS.GREEN_BRIGHT,
  FONT_PRIMARY_BTN_TXT: COLORS.PRIMARY_BTN_TEXT,
};

const Pallet = {
  background: {
    assingnedToYouBg: {
      light: COLORS.GRAY_300,
      dark: COLORS.GRAY_900,
    },
    secondaryBackground: {
      light: COLORS.GRAY_100,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    teacherGuidedTag: {
      light: COLORS.BLUE_MEDIUM,
      dark: COLORS.BLUE_TEACHER_TAG,
    },
    trackerCtaBG: {
      light: COLORS.TEAL_BG,
      dark: COLORS.DARK_BG_QUATERNARY,
    },
    mentorHomeBackground: {
      light: COLORS.LIGHT_BG_SECONDARY,
      dark: COLORS.DARK_BG_PRIMARY,
    },
    studentHomeBackground: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_PRIMARY,
    },
    studentProgressBg: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    scholarClubBg: {
      light: GRADIENTS.SCHOLAR_CLUB_LIGHT,
      dark: COLORS.DARK_BG_QUATERNARY,
    },
    earnPointBg: {
      light: COLORS.ORANGE_BORDER,
      dark: COLORS.ORANGE_POINT_DARK,
    },
    doubtBannerBG: {
      light: GRADIENTS.DOUBT_BANNER_LIGHT,
      dark: GRADIENTS.DOUBT_BANNER_DARK,
    },
    noahCardRoot: {
      light: GRADIENTS.NOAH_CARD_LIGHT,
      dark: GRADIENTS.DOUBT_BANNER_DARK,
    },
    sectionTopicsCardBackground: {
      light: COLORS.LIGHT_BG_CONTENT,
      dark: COLORS.CODE_BG_DARK_SECTION,
    },
    fmbg: {
      light: COLORS.GRAY_300,
      dark: COLORS.DARK_BG_CONTENT,
    },
    proTips: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_CONTENT,
    },
    languageSelectorBg: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_CONTENT,
    },
    fileNameRootbg: {
      light: COLORS.GRAY_50,
      dark: COLORS.DARK_BG_INPUT,
    },

    workBenchBg: {
      light: COLORS.LIGHT_BG_SURFACE,
      dark: COLORS.DARK_BG_PRIMARY,
    },
    background0: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_PRIMARY,
    },
    background1: {
      light: COLORS.LIGHT_BG_SECONDARY,
      dark: COLORS.DARK_BG_PRIMARY,
    },
    background2: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    background3: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_TERTIARY,
    },
    background4: {
      light: COLORS.BLUE_LIGHT,
      dark: COLORS.BLUE_LIGHT,
    },
    background5: {
      light: COLORS.PURPLE_LIGHT,
      dark: COLORS.BLUE_LIGHT,
    },
    background6: {
      light: COLORS.ORANGE_LIGHT,
      dark: COLORS.BLUE_LIGHT,
    },
    background7: { light: COLORS.BLUE_SKY, dark: COLORS.GRAY_900 },
    background8: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_SURFACE,
    },
    background9: { light: COLORS.BLUE_SKY, dark: "#29323D" },
    background10: {
      light: GRADIENTS.GREEN_GRADIENT_LIGHT,
      dark: GRADIENTS.GREEN_GRADIENT_DARK,
    },
    background11: {
      light: GRADIENTS.YELLOW_GRADIENT_LIGHT,
      dark: GRADIENTS.YELLOW_GRADIENT_DARK,
    },
    background30: {
      light: COLORS.LIGHT_BG_CONTENT,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    background31: { light: COLORS.TEAL_BG, dark: COLORS.GRAY_900 },
    background22: {
      light: COLORS.BORDER_LIGHT_PRIMARY,
      dark: COLORS.DARK_BG_QUATERNARY,
    },
    codeEditorBackground: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_CONTENT,
    },
    assignedToYou: {
      light: COLORS.ORANGE_LIGHTER,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    loaderClsbg: { light: COLORS.TEAL_BG, dark: COLORS.DARK_BG_SURFACE },
    completed: {
      light: GRADIENTS.COMPLETED_GRADIENT,
      dark: COLORS.COMPLETED_BG_DARK,
    },
    assessmentBg: { light: COLORS.BLUE_LIGHT, dark: COLORS.GRAY_900 },
    completedSpan: {
      light: COLORS.GREEN_LIGHT,
      dark: COLORS.COMPLETED_SPAN_DARK,
    },
    locked: {
      light: GRADIENTS.LOCKED_LIGHT,
      dark: GRADIENTS.LOCKED_DARK,
    },
    activeBtn: {
      light: COLORS.ACTIVE_BTN,
      dark: COLORS.ACTIVE_BTN,
    },
    tabBtnBg: { light: COLORS.OFF_WHITE, dark: COLORS.GRAY_900 },
    codingclscardbg: {
      light: COLORS.ORANGE_CODING_CARD,
      dark: COLORS.CODING_CARD_DARK,
    },
    worksheetclscardbg: {
      light: COLORS.YELLOW_LIGHT,
      dark: COLORS.WORKSHEET_CARD_DARK,
    },
    runBtnBg: { light: COLORS.GREEN_DARK, dark: COLORS.GREEN_SUCCESS },
    startBtnBg: {
      light: COLORS.ORANGE_DARK,
      dark: COLORS.ORANGE_DARK_MODE,
    },
    InputOutputToggleEditorBtn: {
      light: COLORS.ORANGE_DARK,
      dark: COLORS.ORANGE_DARK,
    },
    highlightBackground: { light: COLORS.ORANGE_HIGHLIGHT, dark: "#2E2724" },
    activeToggleClr: {
      light: IMPORTANT_VALUES.ACTIVE_TOGGLE_CLR_LIGHT,
      dark: IMPORTANT_VALUES.ACTIVE_TOGGLE_CLR_DARK,
    },
    inActiveToggleClr: {
      light: IMPORTANT_VALUES.INACTIVE_TOGGLE_CLR,
      dark: IMPORTANT_VALUES.INACTIVE_TOGGLE_CLR,
    },
    activeToglleBg: {
      light: IMPORTANT_VALUES.ACTIVE_TOGGLE_BG_LIGHT,
      dark: IMPORTANT_VALUES.ACTIVE_TOGGLE_BG_DARK,
    },
    inActiveToggleBg: {
      light: IMPORTANT_VALUES.INACTIVE_TOGGLE_BG_LIGHT,
      dark: IMPORTANT_VALUES.INACTIVE_TOGGLE_BG_DARK,
    },
    roleBackground: {
      light: COLORS.ORANGE_LIGHT,
      dark: COLORS.DARK_BG_SURFACE,
    },
    profileBackground: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    stepperBg: { light: COLORS.TEAL_STEPPER, dark: COLORS.GRAY_900 },
    greenBackground: {
      light: GRADIENTS.GREEN_BG_LIGHT,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    learningPopUpSubject: {
      light: COLORS.WHITE,
      dark: COLORS.GRAY_900,
    },
    searchBackground: {
      light: COLORS.LIGHT_BG_CONTENT,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    profileBackgroundCompleted: {
      light: COLORS.GREEN_LIGHT,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    toggleBg: { light: COLORS.ORANGE_TOGGLE_BG, dark: COLORS.GRAY_900 },
    homeWorkbenchbackground: {
      light: COLORS.BLUE_LIGHT,
      dark: COLORS.DARK_BG_QUATERNARY,
    },
    homeWhiteboardbackground: {
      light: COLORS.PURPLE_LIGHT,
      dark: COLORS.DARK_BG_QUATERNARY,
    },
    homeexercisebackground: {
      light: COLORS.ORANGE_LIGHT,
      dark: COLORS.DARK_BG_QUATERNARY,
    },
    workbenchFileNameRoot: {
      light: COLORS.OFF_WHITE,
      dark: COLORS.DARK_BG_SECONDARY,
    },
    workbenchFileNameContainer: {
      light: COLORS.OFF_WHITE,
      dark: COLORS.DARK_BG_CONTENT,
    },
    wrkbchname: { light: COLORS.OFF_WHITE, dark: COLORS.WRKBCHNAME_DARK },
    filenmrt: { light: COLORS.OFF_WHITE, dark: COLORS.GRAY_900 },
    filetreebg: {
      light: COLORS.WHITE,
      dark: COLORS.GRAY_900,
    },
    fileCollectionClsBg: {
      light: COLORS.WHITE,
      dark: COLORS.FILE_COLLECTION_DARK,
    },
    codngimgclsbg: {
      light: COLORS.ORANGE_CODING_LIGHT,
      dark: COLORS.CODING_IMG_DARK,
    },
    mathsimgclsbg: { light: COLORS.YELLOW_MATHS, dark: COLORS.MATHS_IMG_DARK },
    codingskillclsbg: {
      light: COLORS.ORANGE_CODING_LIGHT,
      dark: COLORS.CODING_SKILL_DARK,
    },
    mathsskillclsbg: {
      light: COLORS.YELLOW_MATHS_SKILL,
      dark: COLORS.MATHS_SKILL_DARK,
    },
    resultBoxBg: {
      light: COLORS.RESULTS_BOX_LIGHT,
      dark: COLORS.RESULTS_BOX_DARK,
    },
    exerciseTabBg: {
      light: COLORS.WHITE,
      dark: COLORS.DARK_BG_INPUT,
    },
    published: {
      light: COLORS.GREEN_BRIGHT,
      dark: COLORS.GREEN_BRIGHT,
    },
    fileBody: { light: COLORS.GRAY_400, dark: COLORS.WRKBCHNAME_DARK },
    learningCardEmptyStateBackground: {
      light: GRADIENTS.LEARNING_CARD_EMPTY_LIGHT,
      dark: GRADIENTS.LEARNING_CARD_EMPTY_DARK,
    },
    cardHighlight: {
      light: COLORS.LIGHT_BG_SURFACE,
      dark: COLORS.GRAY_900,
    },
    imageHighlight: {
      light: COLORS.BORDER_LIGHT_PRIMARY,
      dark: COLORS.GRAY_900,
    },
  },

  // Border Colors
  border2: {
    light: COLORS.BORDER_LIGHT_PRIMARY,
    dark: COLORS.GRAY_900,
  },
  border3: {
    light: COLORS.BORDER_LIGHT_PRIMARY,
    dark: COLORS.DARK_BG_SURFACE,
  },
  border4: {
    light: COLORS.BLUE_BORDER,
    dark: COLORS.BLUE_BORDER,
  },
  border5: { light: COLORS.PURPLE_BORDER, dark: COLORS.BLUE_BORDER },
  border6: { light: "#FBEEDA", dark: COLORS.BLUE_BORDER },
  exerciseTabBorder: {
    light: COLORS.GRAY_300,
    dark: COLORS.GRAY_800,
  },
  languageSelectorBorder: {
    light: `1px solid ${COLORS.GRAY_300}`,
    dark: `1px solid ${COLORS.GRAY_800}`,
  },
  studentProgressBorder: {
    light: `1px solid ${COLORS.BORDER_LIGHT_PRIMARY}`,
    dark: `1px solid ${COLORS.DARK_BG_SECONDARY}`,
  },
  mentorDbLeftRoot: {
    light: `1px solid ${COLORS.BORDER_LIGHT_PRIMARY}`,
    dark: `1px solid ${COLORS.GRAY_900}`,
  },
  borderTopicsCard: {
    light: COLORS.GRAY_200,
    dark: COLORS.GRAY_900,
  },
  borderExerciseCompleted: {
    light: COLORS.GREEN_COMPLETED_BORDER,
    dark: COLORS.GRAY_900,
  },
  profileBorder: {
    light: COLORS.BORDER_LIGHT_TERTIARY,
    dark: COLORS.GRAY_900,
  },
  workbenchFileNameContainer: {
    light: `1px solid ${COLORS.BORDER_LIGHT_TERTIARY}`,
    dark: `1px solid ${COLORS.WRKBCHNAME_DARK}`,
  },
  InputOutputToggleEditorBtnBorder: {
    light: `1px solid ${COLORS.ORANGE_DARK}`,
    dark: `1px solid ${COLORS.ORANGE_DARK}`,
  },
  border7: {
    light: `1px solid ${COLORS.GRAY_200}`,
    dark: `1px solid ${COLORS.DARK_BG_SURFACE}`,
  },
  border8: {
    light: `1px solid ${COLORS.BORDER_LIGHT_TERTIARY}`,
    dark: `1px solid ${COLORS.GRAY_900}`,
  },
  dividerbackground: {
    light: COLORS.TRANSPARENT_BLACK,
    dark: COLORS.DARK_BG_SURFACE,
  },
  borderselectLang: {
    light: `1px solid #7D7D7D`,
    dark: `1px solid ${COLORS.TEXT_DARK_SECONDARY}`,
  },
  bordereditorborder: {
    light: `1px solid ${COLORS.GRAY_400}`,
    dark: `1px solid ${COLORS.DARK_BG_SURFACE}`,
  },
  filetreeborder: {
    light: `1px solid ${COLORS.BORDER_LIGHT_TERTIARY}`,
    dark: `1px solid ${COLORS.DARK_BG_SECONDARY}`,
  },
  searchBorder: { light: COLORS.GRAY_200, dark: COLORS.DARK_BG_SURFACE },
  searchBorderOuter: {
    light: COLORS.BLUE_BLUE_BORDER_LIGHT,
    dark: COLORS.DARK_BG_SURFACE,
  },
  completedBorder: {
    light: COLORS.GREEN_BORDER,
    dark: COLORS.COMPLETED_BORDER_DARK,
  },
  tabLayoutBorder: {
    light: COLORS.BORDER_LIGHT_TERTIARY,
    dark: COLORS.DARK_BG_SURFACE,
  },
  btnborder: {
    light: `1.6px solid ${COLORS.TEAL_BORDER}`,
    dark: `1.6px solid ${COLORS.ORANGE_DARK_MODE}`,
  },
  dateAndMonth: {
    light: COLORS.TEAL_DATE,
    dark: COLORS.TEXT_DARK_SECONDARY,
  },

  // Shadows
  shadow1: {
    light: SHADOWS.LIGHT_SHADOW,
    dark: SHADOWS.DARK_SHADOW,
  },
  completedShadow: {
    light: SHADOWS.COMPLETED_SHADOW_LIGHT,
    dark: SHADOWS.COMPLETED_SHADOW_DARK,
  },
  tabLayoutShadow: {
    light: SHADOWS.TAB_LAYOUT_SHADOW_LIGHT,
    dark: SHADOWS.TAB_LAYOUT_SHADOW_DARK,
  },
  shadowLearningTopic: {
    light: SHADOWS.LEARNING_TOPIC_SHADOW,
    dark: SHADOWS.LEARNING_TOPIC_SHADOW,
  },

  // Text colors and other properties
  assingnedToYou: {
    light: COLORS.GRAY_600,
    dark: COLORS.BROWN_DARK,
  },
  heading: {
    light: COLORS.TEAL_PRIMARY,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  newHeading: {
    light: COLORS.BLACK,
    dark: COLORS.WHITE,
  },
  subHeading: {
    light: COLORS.BLACK,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  points: {
    light: COLORS.BLACK,
    dark: COLORS.GRAY_500,
  },
  viewDetailClr: {
    light: COLORS.TEAL_LIGHT,
    dark: COLORS.BROWN_DARK,
  },
  inActiveArrow: {
    light: COLORS.TEAL_LIGHT,
    dark: COLORS.BROWN_DARK,
  },
  activeArrow: {
    light: COLORS.TEAL_PRIMARY,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  classTimeClr: {
    light: COLORS.TEAL_LIGHTER,
    dark: COLORS.BROWN_DARK,
  },
  trackerTextClr: {
    light: COLORS.GRAY_600,
    dark: COLORS.BROWN_DARK,
  },
  trackerHeading: {
    light: COLORS.GRAY_900,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  dividerMD: { light: COLORS.DIVIDER_LIGHT, dark: COLORS.GRAY_900 },
  teacherGuidedTagClr: {
    light: COLORS.TEACHER_TAG_CLR,
    dark: COLORS.TEXT_DARK_SECONDARY,
  },
  body: { light: COLORS.TEAL_SECONDARY, dark: COLORS.BROWN_DARK },
  showingNo: {
    light: COLORS.TEAL_SECONDARY,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  sectionText: {
    light: COLORS.TEAL_SECONDARY,
    dark: COLORS.TEXT_DARK_SECONDARY,
  },
  newSectionText: { light: COLORS.GRAY_500, dark: COLORS.TEXT_DARK_SECONDARY },
  highLightTextTopicCard: {
    light: COLORS.GRAY_500,
    dark: GRADIENTS.HIGHLIGHT_TEXT_DARK,
  },
  languageselectclr: { light: "#959595", dark: COLORS.TEXT_DARK_SECONDARY },
  caption: {
    light: COLORS.TEAL_LIGHT,
    dark: COLORS.BROWN_DARK,
  },
  caption1: { light: COLORS.YELLOW_MEDIUM, dark: COLORS.BROWN_DARK },
  highlightColor: {
    light: COLORS.ORANGE_DEEP,
    dark: COLORS.ORANGE_DARK_MODE,
  },
  highlightColor1: { light: COLORS.ORANGE_MEDIUM, dark: COLORS.ORANGE_MEDIUM },
  roleColor: { light: COLORS.BROWN_LIGHT, dark: COLORS.BROWN_DARK },
  codeEditortxt: { light: COLORS.DARK_BG_SECONDARY, dark: COLORS.GRAY_300 },
  menucolor: { light: "#959595", dark: COLORS.TEXT_DARK_SECONDARY },
  exercise: {
    light: COLORS.GRAY_600,
    dark: COLORS.TEXT_DARK_SECONDARY,
  },
  exerciseCount: {
    light: COLORS.GRAY_900,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  recentlyOpened: {
    light: COLORS.TEAL_PRIMARY,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  searchText: {
    light: COLORS.BLACK,
    dark: COLORS.TEXT_DARK_SECONDARY,
  },
  fileText: { light: COLORS.BLACK, dark: COLORS.WHITE },
  lastOpened: { light: COLORS.GRAY_400, dark: COLORS.BROWN_DARK },
  assessment: { light: COLORS.BLUE_DARK, dark: COLORS.BROWN_DARK },
  level: { light: COLORS.GRAY_600, dark: COLORS.BROWN_MUTED },
  completed: { light: COLORS.GREEN_MEDIUM, dark: COLORS.GREEN_COMPLETED }, // Font color
  tickSymbol: { light: COLORS.BLUE_DARK, dark: COLORS.GREEN_TICK },
  exercisePgTitle: {
    light: COLORS.GRAY_900,
    dark: COLORS.TEXT_HEADING_DARK,
  },
  label: { light: COLORS.GRAY_600, dark: COLORS.TEXT_DARK_SECONDARY },
  filter: { light: COLORS.GRAY_700, dark: COLORS.TEXT_DARK_SECONDARY },
  filterIcon: { light: COLORS.GRAY_700, dark: COLORS.FILTER_ICON_DARK },
  rowHeading: { light: COLORS.BLUE_LINK, dark: COLORS.TEXT_DARK_PRIMARY },
  trackerHeadings: {
    light: COLORS.GRAY_600,
    dark: COLORS.TEXT_DARK_PRIMARY,
  },
  report: { light: COLORS.GRAY_600, dark: COLORS.TEXT_DARK_SECONDARY },
  toggleEditorOutputcolor: { light: COLORS.WHITE, dark: undefined },
  toggleEditornotOutputcolor: {
    light: COLORS.ORANGE_DARK,
    dark: undefined,
  },
  textareaautosizeClsclr: {
    light: COLORS.DARK_BG_SECONDARY,
    dark: COLORS.WHITE,
  },
  buttonclr: {
    light: COLORS.TEAL_PRIMARY,
    dark: COLORS.ORANGE_DARK_MODE,
  },
  codingskillclsclr: { light: COLORS.RED_DARK, dark: COLORS.RED_LIGHT },
  mathsskillclsclr: { light: COLORS.MATHS_SKILL_LIGHT, dark: "#C8BFA7" },
  resultBoxText: {
    light: COLORS.GRAY_900,
    dark: COLORS.TEXT_DARK_SECONDARY,
  },
  primaryBtnTxt: {
    light: COLORS.PRIMARY_BTN_TEXT,
    dark: COLORS.PRIMARY_BTN_TEXT,
  },
  secondaryBtnTxt: {
    light: COLORS.PRIMARY_BTN_TEXT,
    dark: COLORS.YELLOW_BRIGHT,
  },
  secondaryBtnBdr: {
    light: `1px solid ${COLORS.GRAY_300}`,
    dark: "none",
  },
  ternaryBtnBdr: {
    light: `1px solid ${COLORS.GRAY_300}`,
    dark: `1px solid ${COLORS.YELLOW_BRIGHT}`,
  },
  secondaryBtnHover: {
    light: COLORS.GRAY_100,
    dark: COLORS.GRAY_900,
  },
};

// Function to create a theme based on mode (remains the same as the previous response)
export const createNewTheme = (mode) => {
  const newPalette = {
    mode: mode,
    spacing: 8,
    background: {},
  };

  for (const key in Pallet) {
    if (key === "background") {
      for (const bgKey in Pallet.background) {
        newPalette.background[bgKey] = Pallet.background[bgKey][mode];
      }
    } else {
      newPalette[key] = Pallet[key][mode];
    }
  }

  return createTheme({
    palette: newPalette,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        ipadPro: 1025,
        lg: 1280,
        xl: 1920,
      },
    },
  });
};

export const lightTheme = createNewTheme("light");
export const darkTheme = createNewTheme("dark");

export const getTheme = () => {
  const isDarkMode = store.getState().darkModeTheme.isDarkModeTheme;
  if (isDarkMode) {
    return darkTheme;
  } else {
    return lightTheme;
  }
};
